import React, {type ReactElement} from "react";

import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";

import Quote, {QuoteProps} from "../cards/quote/Quote";
import ModuleBody from "../layouts/module-body/ModuleBody";

import * as styles from "./quotes.module.scss";

interface QuotesProps {
  autoplay?: boolean;
  classes?: string;
  quotes: Array<QuoteProps>;
  darkMode?: boolean;
  isFormLayout?: boolean;
}

export const parseContentfulQuoteData = (
  data: Queries.QuoteModuleFragmentFragment
): QuotesProps => {
  const {autoplay, theme = "dark", slides} = data;
  return {
    autoplay: autoplay === true,
    darkMode: (theme || "dark") === "dark",
    quotes: slides
      ? slides.map(item => {
          return {
            quote: item?.quote,
            name: item?.name,
            role: item?.role,
            image: item?.image,
            largeImage: item?.largeImage,
            darkMode: theme === "dark",
          } as QuoteProps;
        })
      : [],
  };
};

const AUTOPLAY_INTERVAL = 5000;

/**
 * Component that behaves like a carousel and displays quotes.
 * Pager will not be rendered if there is only one quote.
 */
const Quotes = (props: QuotesProps): ReactElement => {
  const {
    autoplay,
    classes,
    quotes,
    darkMode = true,
    isFormLayout = false,
  } = props;

  const [slideIndex, setSlideIndex] = React.useState(0);
  const autoplayIndex = React.useRef(0);
  const now = React.useRef(Date.now());
  const timer = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * setInterval callback that changes slide index.
   */
  const changeQuote = React.useCallback(() => {
    if (autoplayIndex.current === quotes.length - 1) {
      autoplayIndex.current = 0;
    } else {
      autoplayIndex.current += 1;
    }

    setSlideIndex(autoplayIndex.current);
  }, [quotes.length]);

  /**
   * Creates unique-ish ID to use as key.
   * @param {string} string
   * @returns {string}
   */
  const makeKeyID = (string: string): string => {
    return `${encodeURIComponent(string.toLowerCase())}-${now.current}`;
  };

  /**
   * Click handler for pager button and updates slide index.
   * Resets autoplay interval if auto-playing.
   * @param {number} index
   */
  const pagerButtonClick = (index: number) => {
    if (autoplay === true && timer.current !== null) {
      clearInterval(timer.current);
      autoplayIndex.current = index;
      timer.current = setInterval(changeQuote, AUTOPLAY_INTERVAL);
    }

    setSlideIndex(index);
  };

  /**
   * Clear interval timer on unmount.
   */
  React.useEffect(() => {
    return function unmount() {
      if (timer.current !== null) {
        clearInterval(timer.current);
      }
    };
  }, []);

  /**
   * Fire setInterval if we need to autoplay.
   */
  React.useEffect(() => {
    if (autoplay === true && quotes.length > 1) {
      timer.current = setInterval(changeQuote, AUTOPLAY_INTERVAL);
    }
  }, [autoplay, changeQuote, quotes.length]);

  return (
    <section
      className={css(
        styles.quotes,
        isFormLayout ? styles.quotesFormLayout : "",
        darkMode ? styles.quotesDark : "",
        classes!
      )}
    >
      <ModuleBody>
        <div className={css(styles.quotes__viewport)}>
          <ul
            className={css(styles.quotes__slides)}
            style={{transform: `translateX(${slideIndex * -100}%)`}}
          >
            {quotes.map((item, index) => {
              return (
                <li
                  key={makeKeyID(`slide-${index}`)}
                  className={css(
                    styles.quotes__slide,
                    index === slideIndex ? styles.quotes__slideActive : ""
                  )}
                >
                  <Quote
                    darkMode={darkMode}
                    image={item?.image}
                    largeImage={item?.largeImage}
                    name={item?.name}
                    quote={item?.quote}
                    role={item?.role}
                    isFormLayout={isFormLayout}
                  />
                </li>
              );
            })}
          </ul>
          {quotes.length > 1 ? (
            <ul className={css(styles.quotes__pager)}>
              {quotes.length > 1 &&
                quotes.map((item, index) => {
                  return (
                    <li
                      key={makeKeyID(`pager-${index}`)}
                      className={css(styles.quotes__pagerItem)}
                    >
                      <button
                        type="button"
                        className={css(
                          styles.quotes__pagerButton,
                          index === slideIndex
                            ? styles.quotes__pagerButtonActive
                            : ""
                        )}
                        aria-label={`${translate("item")} ${index}`}
                        onClick={() => pagerButtonClick(index)}
                      />
                    </li>
                  );
                })}
            </ul>
          ) : null}
        </div>
      </ModuleBody>
    </section>
  );
};

export default Quotes;
